import { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'

import FormHelperText from '@material-ui/core/FormHelperText'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'

import {
  DEFAULT_SENDER_EMAIL,
  buildSenderEmail,
  getSenderDomainOptions,
  splitSenderEmail
} from '../../functions/sender-email.function'

import { Row, Mailbox, Domain } from './style'

const YOURVIEWS_DOMAIN = splitSenderEmail(DEFAULT_SENDER_EMAIL).domain

/**
 * Sender email as mailbox + allowed domain. Without the store settings
 * (AllowedDomains null) it falls back to a plain email field, validated
 * only by the backend.
 */
const SenderEmailField = ({
  id,
  value,
  onChange,
  onBlur,
  settings,
  error,
  helperText,
  t
}) => {
  const { mailbox, domain: valueDomain } = splitSenderEmail(value)
  const options = getSenderDomainOptions(settings?.AllowedDomains, valueDomain)
  const [domain, setDomain] = useState(valueDomain || options[0]?.value)

  useEffect(() => {
    if (valueDomain) setDomain(valueDomain)
  }, [valueDomain])

  if (!settings?.AllowedDomains)
    return (
      <TextField
        id={id}
        required
        fullWidth
        size="small"
        type="email"
        variant="outlined"
        label={t('campaing.edit.form.email.senderEmailLabel')}
        placeholder={t('campaing.edit.form.email.senderEmailDesc')}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value.trim())}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        inputProps={{ 'data-testid': `${id}-email` }}
      />
    )

  const isYourviews = domain === YOURVIEWS_DOMAIN

  const handleChangeDomain = (newDomain) => {
    setDomain(newDomain)
    onChange(buildSenderEmail(isYourviews ? '' : mailbox, newDomain))
  }

  return (
    <div>
      <Row>
        <Mailbox>
          <TextField
            id={id}
            required
            fullWidth
            size="small"
            variant="outlined"
            label={t('campaing.edit.form.email.senderEmailLabel')}
            placeholder={t('campaign.fields.email.senderMailboxPlaceholder')}
            value={isYourviews ? splitSenderEmail(DEFAULT_SENDER_EMAIL).mailbox : mailbox}
            disabled={isYourviews}
            onChange={(e) =>
              onChange(buildSenderEmail(e.target.value.replace(/@/g, ''), domain))
            }
            onBlur={onBlur}
            error={error}
            InputProps={{
              endAdornment: <InputAdornment position="end">@</InputAdornment>
            }}
            inputProps={{ 'data-testid': `${id}-mailbox` }}
          />
        </Mailbox>
        <Domain>
          <TextField
            id={`${id}-domain`}
            select
            fullWidth
            size="small"
            variant="outlined"
            label={t('campaign.fields.email.senderDomain')}
            value={domain ?? ''}
            onChange={(e) => handleChangeDomain(e.target.value)}
            error={error && !options.find((o) => o.value === domain)?.allowed}
            SelectProps={{ native: true }}
            inputProps={{ 'data-testid': `${id}-domain` }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.yourviews
                  ? t('campaign.fields.email.senderDomainYourviews', {
                      defaultEmail: DEFAULT_SENDER_EMAIL
                    })
                  : option.allowed
                  ? option.value
                  : t('campaign.fields.email.senderDomainNotAllowed', {
                      domain: option.value
                    })}
              </option>
            ))}
          </TextField>
        </Domain>
      </Row>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </div>
  )
}

export default withTranslation()(SenderEmailField)
